/** 鉴权状态 + token 持久化.
 * 设计:单一 Zustand store + localStorage,其他组件用 hook 订阅。
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface UserInfo {
  id: string; // 雪花 BIGINT,后端序列化为字符串
  username: string;
  real_name: string | null;
  email: string | null;
  roles: string[];
  is_active: boolean;
}

interface AuthState {
  sessionEpoch: number;
  accessToken: string | null;
  refreshToken: string | null;
  user: UserInfo | null;
  loggedIn: boolean;
  setTokens: (access: string, refresh: string) => void;
  refreshTokens: (expected: string, access: string, refresh: string) => boolean;
  setUser: (user: UserInfo) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      sessionEpoch: 0,
      accessToken: null,
      refreshToken: null,
      user: null,
      loggedIn: false,
      setTokens: (access, refresh) =>
        set((state) => ({
          accessToken: access,
          refreshToken: refresh,
          loggedIn: true,
          sessionEpoch: (state.sessionEpoch + 1) >>> 0,
        })),
      refreshTokens: (expected, access, refresh) => {
        let applied = false;
        set((state) => {
          if (state.refreshToken !== expected || !state.loggedIn) return state;
          applied = true;
          return { accessToken: access, refreshToken: refresh };
        });
        return applied;
      },
      setUser: (user) => set({ user }),
      logout: () =>
        set((state) => ({
          accessToken: null,
          refreshToken: null,
          user: null,
          loggedIn: false,
          sessionEpoch: (state.sessionEpoch + 1) >>> 0,
        })),
    }),
    {
      name: "trust.auth",
      // 只存 token + user,排除方法
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        loggedIn: s.loggedIn,
      }),
    },
  ),
);

export function currentRoles(): Set<string> {
  return new Set(useAuthStore.getState().user?.roles ?? []);
}

export function hasRole(...any: string[]): boolean {
  const roles = currentRoles();
  return any.some((r) => roles.has(r));
}
